import { useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/auth-store";
import { useAuthRefresh } from "../hooks/use-auth-refresh";

import { AppConfig } from "../config";
import { useToast } from "../hooks/use-toast";
import { handleMutationError } from "@/lib/utils";
import { Loader } from "@/components/loader";

export const AuthWrapper = ({ children }: { children: React.ReactNode }) => {
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const initializeAuth = useAuthStore((state) => state.initializeAuth);
  const loginWithGoogle = useAuthStore((state) => state.loginWithGoogle);

  const navigate = useNavigate();
  const { toast } = useToast();

  // Set up automatic auth refresh on visibility changes
  useAuthRefresh();

  // Check auth status and handle OAuth redirect
  useEffect(() => {
    // First, immediately initialize from storage (for instant login in new tabs)
    initializeAuth();

    // Then do async check to validate the session
    const checkInitialAuth = async () => await checkAuth();
    checkInitialAuth();

    // Check for auth code in URL
    const params = new URLSearchParams(window.location.search);
    const state = params.get("state");
    const rawQuery = window.location.search.slice(1); // remove "?"
    const match = rawQuery.match(/(?:^|&)code=([^&]*)/);
    const code = match ? match[1] : null;

    const hasGoogleLoginError = window.location.search.includes("error");
    if (
      code &&
      window.location.pathname === "/auth/callback" &&
      !hasGoogleLoginError
    ) {
      // Clean URL
      window.history.replaceState({}, document.title, "/");

      // Login with the authorization code
      loginWithGoogle(code, `${AppConfig.baseUrl}/auth/callback`)
        .then(() => {
          if (!state) {
            navigate("/", { replace: true });
            return;
          }

          try {
            const targetPath =
              JSON.parse(decodeURIComponent(state)).from || "/";
            navigate(targetPath, { replace: true });
          } catch {
            navigate("/", { replace: true });
          }
        })
        .catch((error) => {
          handleMutationError(error);
          navigate("/login", { replace: true });
        });
    } else if (hasGoogleLoginError) {
      navigate("/login", { replace: true });
    }
  }, [checkAuth, initializeAuth, loginWithGoogle, navigate, toast]);

  return <>{children}</>;
};

// Protected route wrapper
export const ProtectedRoute = ({
  element,
  requiredEntityType,
  requiredAction,
  requiredGroup,
}: {
  element: React.ReactNode;
  requiredEntityType?: string;
  requiredAction?: string | string[];
  requiredGroup?: string;
}) => {
  const { isAuthenticated, isLoading, hasEntityAction, isInGroup } =
    useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return <Loader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check group requirement
  if (requiredGroup && !isInGroup(requiredGroup)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check entity action requirement
  if (requiredEntityType && requiredAction) {
    if (!hasEntityAction(requiredEntityType, requiredAction)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{element}</>;
};
